/** The two conversions between a stored note and an editable document.
 *
 * A rich-text editor works on a DOM; a note is stored as text with markers
 * (see richText.tsx for why). These are the edges where the two meet, and
 * they are deliberately pure functions in their own file: they carry the
 * whole safety story, so they should be readable and exhaustively testable
 * without rendering anything.
 *
 * The safety story, in one line: `fromDom` understands exactly three marks
 * and text, and everything else it walks through contributes only its text.
 * So a learner who pastes a page of styled HTML — or a script tag — gets
 * their words and nothing else, and there is never any HTML to sanitise on
 * the way back out, because what we stored was never HTML.
 */
import { parseNote } from "./richText";

const ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
};

const escapeHtml = (text: string) => text.replace(/[&<>"]/g, (c) => ESCAPES[c]);

/** Stored note → the HTML the editor starts from.
 *
 *  Only ever produces strong/em/mark and escaped text, so it cannot
 *  introduce anything the editor would have to defend against. */
export function toHtml(note: string): string {
  if (!note) return "";
  return parseNote(note)
    .map((piece) => {
      // Newlines are <br>: a contentEditable would otherwise collapse them,
      // and a note is mostly short lines.
      const text = escapeHtml(piece.text).replace(/\n/g, "<br>");
      if (piece.kind === "bold") return `<strong>${text}</strong>`;
      if (piece.kind === "italic") return `<em>${text}</em>`;
      if (piece.kind === "mark") return `<mark>${text}</mark>`;
      return text;
    })
    .join("");
}

/** Which of our three marks, if any, this element represents.
 *
 *  Browsers disagree about what their own bold command produces — `<b>` in
 *  some, `<strong>` in others, and a styled `<span>` when the document is
 *  in CSS mode — so the tag alone is not enough to go on. */
function markOf(el: Element): "bold" | "italic" | "mark" | null {
  const tag = el.tagName.toLowerCase();
  if (tag === "strong" || tag === "b") return "bold";
  if (tag === "em" || tag === "i") return "italic";
  if (tag === "mark") return "mark";

  const style = (el as HTMLElement).style;
  if (style) {
    const weight = style.fontWeight;
    if (weight === "bold" || weight === "bolder" || Number(weight) >= 600) {
      return "bold";
    }
    if (style.fontStyle === "italic") return "italic";
    // execCommand("hiliteColor") produces a background, not a <mark>.
    if (style.backgroundColor && style.backgroundColor !== "transparent") {
      return "mark";
    }
  }
  return null;
}

const WRAP: Record<"bold" | "italic" | "mark", string> = {
  bold: "**",
  italic: "*",
  mark: "==",
};

/** The editable document → the note to store.
 *
 *  `inside` is the mark we are already within, so nesting the same mark
 *  twice does not emit two pairs of markers — the browser is entitled to
 *  produce `<strong><strong>x</strong></strong>` after enough toggling, and
 *  `****x****` would read back as a bold asterisk. */
export function fromDom(node: Node, inside: string[] = []): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";

  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  const el = node as Element;
  const tag = el.tagName.toLowerCase();

  // A script or style contributes nothing at all — not even its text.
  if (tag === "script" || tag === "style") return "";
  if (tag === "br") return "\n";

  const mark = markOf(el);
  const nested = mark && !inside.includes(mark) ? [...inside, mark] : inside;
  let inner = "";
  el.childNodes.forEach((child) => {
    inner += fromDom(child, nested);
  });

  // Block elements (a pasted paragraph, or the div a browser makes on
  // Enter) become a line of their own.
  const BLOCK = /^(div|p|li|h[1-6]|blockquote|tr)$/;
  if (BLOCK.test(tag)) inner = inner.endsWith("\n") ? inner : `${inner}\n`;

  if (!mark || inside.includes(mark)) return inner;
  // An empty mark would store `****`, which reads back as nothing useful.
  if (!inner.trim()) return inner;
  // Markers have to sit against the words, or `** bold **` fails to parse,
  // so any spaces the selection caught are pushed outside them. `core` is
  // known non-empty — the blank case returned above.
  const core = inner.trim();
  const at = inner.indexOf(core);
  const wrap = WRAP[mark];
  return `${inner.slice(0, at)}${wrap}${core}${wrap}${inner.slice(at + core.length)}`;
}

/** The editable element → the note to store, with the trailing newline a
 *  browser leaves behind trimmed off. */
export function noteFromElement(el: HTMLElement): string {
  let out = "";
  el.childNodes.forEach((child) => {
    out += fromDom(child);
  });
  return out.replace(/\n+$/, "");
}
